import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { UserProfileHelper, UserProfile } from '@lura/shared'
import type { DeviceDataRow } from '@lura/shared'
import { TopNavigation } from '@/components/TopNavigation'
import { Submenu } from '@/components/Submenu'
import { InfoCard } from '@/components/InfoCard'
import { ProgressBar } from '@/components/ProgressBar'
import { DataTable } from '@/components/DataTable'
import { PHChart } from '@/components/charts/PHChart'
import { TemperatureChart } from '@/components/charts/TemperatureChart'
import { supabase } from '@lura/shared'
import { PatientId } from '@/components/PatientId'

interface PHData {
  time: number
  value: number
}

interface TemperatureData {
  time: number
  value: number
}

export function PatientPage() {
  const { patientId } = useParams<{ patientId: string }>()
  const { user, signOut } = useAuth()
  const location = useLocation()
  useNavigate() // Available for future use

  // Patient State
  const [patient, setPatient] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // pH State
  const [phData, setPhData] = useState<PHData[]>([])
  const [currentPH, setCurrentPH] = useState(5.8)
  const [averagePH, setAveragePH] = useState(6.2)
  const [phWearTime] = useState('00:00:00')

  // Temperature State
  const [temperatureData, setTemperatureData] = useState<TemperatureData[]>([])
  const [currentTemp, setCurrentTemp] = useState(37.2)
  const [averageTemp, setAverageTemp] = useState(37.1)

  const [batteryVoltage, setBatteryVoltage] = useState('0V')
  const [batteryPercentage, setBatteryPercentage] = useState(0)
  const [rawDataRows, setRawDataRows] = useState<DeviceDataRow[]>([])

  // Pagination state for raw data
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const rowsPerPage = 50

  // Submenu items
  const submenuItems = [
    { id: 'dashboard', label: 'Dashboard', href: `/patient/${patientId}` },
    {
      id: 'raw-data',
      label: 'Raw Data',
      href: `/patient/${patientId}/raw-data`
    }
  ]

  // Derive active tab from URL
  const activeTab = location.pathname.endsWith('/raw-data')
    ? 'raw-data'
    : 'dashboard'

  // Check if user is clinician (only clinicians can access patient health data)
  const isClinician = UserProfileHelper.isClinician(user)

  // Fetch patient data
  useEffect(() => {
    const fetchPatient = async () => {
      if (!patientId) return

      try {
        setLoading(true)
        setError(null)

        const { data, error: fetchError } = await supabase.rpc(
          'get_patient_profile',
          { p_patient_id: patientId }
        )

        if (fetchError) {
          console.error('Error fetching patient:', fetchError)
          setError('Failed to load patient data')
          return
        }

        if (!data || data.length === 0) {
          setError('Patient not found')
          return
        }

        setPatient(new UserProfile(data[0]))
      } catch (err) {
        console.error('Error fetching patient:', err)
        setError('Failed to load patient data')
      } finally {
        setLoading(false)
      }
    }

    fetchPatient()
  }, [patientId])

  // Fetch device data from Supabase
  const fetchDeviceData = async () => {
    if (!patientId) return

    try {
      if (activeTab === 'raw-data') {
        // Fetch paginated raw data
        const offset = (currentPage - 1) * rowsPerPage

        // Get total count
        const { count } = await supabase
          .from('device_data')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', patientId)

        if (count !== null) {
          setTotalRows(count)
        }

        // Get paginated data
        const { data, error } = await supabase
          .from('device_data')
          .select('*')
          .eq('user_id', patientId)
          .order('recorded_at', { ascending: false })
          .range(offset, offset + rowsPerPage - 1)

        if (error) {
          console.error('Error fetching raw data:', error)
          return
        }

        if (data) {
          setRawDataRows(data as DeviceDataRow[])
        }
      } else {
        // Fetch last 5 minutes for dashboard
        const fiveMinutesAgo = new Date(Date.now() - 300000).toISOString()

        const { data, error } = await supabase
          .from('device_data')
          .select('*')
          .eq('user_id', patientId)
          .gte('recorded_at', fiveMinutesAgo)
          .order('recorded_at', { ascending: true })

        if (error) {
          console.error('Error fetching device data:', error)
          return
        }

        if (data && data.length > 0) {
          setRawDataRows(data as DeviceDataRow[])

          // Process data for charts
          const phChartData: PHData[] = []
          const tempChartData: TemperatureData[] = []

          data.forEach((row: DeviceDataRow) => {
            const timestamp = new Date(row.recorded_at).getTime()

              phChartData.push({
              time: timestamp,
              value: row.calibrated_ph
            })

            tempChartData.push({
              time: timestamp,
              value: row.calibrated_temp
            })
          })

          setPhData(phChartData)
          setTemperatureData(tempChartData)

          // Update current values (latest readings)
          if (phChartData.length > 0) {
            const latestPh = phChartData[phChartData.length - 1].value
            setCurrentPH(Number(latestPh.toFixed(1)))

            const avgPh =
              phChartData.reduce((sum, d) => sum + d.value, 0) /
              phChartData.length
            setAveragePH(Number(avgPh.toFixed(1)))
          }

          if (tempChartData.length > 0) {
            const latestTemp = tempChartData[tempChartData.length - 1].value
            setCurrentTemp(Number(latestTemp.toFixed(1)))

            const avgTemp =
              tempChartData.reduce((sum, d) => sum + d.value, 0) /
              tempChartData.length
            setAverageTemp(Number(avgTemp.toFixed(1)))
          }

          // Update battery voltage and percentage from latest reading
          const latestReading = data[data.length - 1] as DeviceDataRow
          setBatteryVoltage(
            `${(latestReading.battery_voltage_mv / 1000).toFixed(2)}V`
          )
          setBatteryPercentage(
            Math.round((latestReading.battery_voltage_mv / 3000) * 100)
          )
        }
      }
    } catch (err) {
      console.error('Error fetching device data:', err)
    }
  }

  // Initial data load and refetch when tab changes or page changes
  useEffect(() => {
    if (patientId && isClinician) {
      fetchDeviceData()
    }
  }, [patientId, activeTab, isClinician, currentPage])

  // Poll for new data every 1 second (only on dashboard)
  useEffect(() => {
    if (!patientId || !isClinician || activeTab !== 'dashboard') return

    const interval = setInterval(() => {
      fetchDeviceData()
    }, 1000) // 1 second

    return () => clearInterval(interval)
  }, [patientId, isClinician, activeTab])

  // Early return checks after all hooks have been called
  if (!user?.profile) return null

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white">
        <TopNavigation
          currentPage="Patients"
          onSignOut={signOut}
          user={user}
        />
        <div className="max-w-7xl mx-auto py-8 px-6">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!patient || error) {
    return (
      <div className="min-h-screen bg-off-white">
        <TopNavigation
          currentPage="Patients"
          onSignOut={signOut}
          user={user}
        />
        <div className="max-w-7xl mx-auto py-8 px-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Patient not found
            </h2>
            <a
              href="/patients"
              className="text-indigo-600 hover:text-indigo-800 mt-4 inline-block"
            >
              Return to Patients
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Patients"
        onSignOut={signOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Back Button */}
          <button
            onClick={() => (window.location.href = '/patients')}
            className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Patients
          </button>

          <div className="mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              <PatientId patientId={patient.id} size="lg" />
            </h2>
            <p className="text-lg text-gray-600 mt-2">
              Last active:{' '}
              {new Date(
                patient.updated_at || patient.created_at
              ).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>

          {/* Submenu - Only show for clinicians */}
          {isClinician && (
            <div className="mb-8">
              <Submenu items={submenuItems} activeItem={activeTab} />
            </div>
          )}

          {/* Dashboard View */}
          {activeTab === 'dashboard' && isClinician && (
            <>
              {/* Top card - Total Wear Time and Device Battery */}
              <div className="bg-light-green rounded-3xl p-6 shadow-sm mb-8">
                <div className="grid grid-cols-2 gap-6">
                  <InfoCard title="Total Wear Time" value={phWearTime} nested />
                  <div className="bg-white rounded-xl p-5 shadow-xs">
                    <ProgressBar
                      progress={batteryPercentage}
                      title="Device Battery"
                    />
                  </div>
                </div>
              </div>

              {/* Bottom two cards - pH and Temperature */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* pH Card */}
                <div className="bg-light-green rounded-3xl p-6 shadow-sm">
                  <h3
                    className="text-xl font-semibold text-gray-800 mb-6"
                    style={{ fontFamily: 'Family' }}
                  >
                    pH Monitoring
                  </h3>

                  <div className="space-y-6">
                    <div className="bg-white rounded-xl p-6 shadow-xs">
                      <PHChart data={phData} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InfoCard
                        title="Current pH"
                        value={currentPH.toFixed(1)}
                        nested
                      />
                      <InfoCard
                        title="Average pH"
                        value={averagePH.toFixed(1)}
                        nested
                      />
                    </div>

                    <DataTable
                      title="Last 10 pH Readings"
                      headers={['Timestamp', 'mV', 'pH']}
                      data={phData.slice(-10).map(reading => ({
                        timestamp: new Date(reading.time).toLocaleTimeString(),
                        mV: `${(reading.value * 59.16).toFixed(1)}`,
                        value: reading.value.toFixed(1)
                      }))}
                      batteryVoltage={batteryVoltage}
                      nested
                    />
                  </div>
                </div>

                {/* Temperature Card */}
                <div className="bg-light-green rounded-3xl p-6 shadow-sm">
                  <h3
                    className="text-xl font-semibold text-gray-800 mb-6"
                    style={{ fontFamily: 'Family' }}
                  >
                    Temperature Monitoring
                  </h3>

                  <div className="space-y-6">
                    <div className="bg-white rounded-xl p-6 shadow-xs">
                      <TemperatureChart data={temperatureData} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InfoCard
                        title="Current Temp"
                        value={`${currentTemp.toFixed(1)}°C`}
                        nested
                      />
                      <InfoCard
                        title="Average Temp"
                        value={`${averageTemp.toFixed(1)}°C`}
                        nested
                      />
                    </div>

                    <DataTable
                      title="Last 10 Temperature Readings"
                      headers={['Timestamp', 'mV', '°C']}
                      data={temperatureData.slice(-10).map(reading => ({
                        timestamp: new Date(reading.time).toLocaleTimeString(),
                        mV: `${(reading.value * 27.3).toFixed(1)}`,
                        value: reading.value.toFixed(1)
                      }))}
                      batteryVoltage={batteryVoltage}
                      nested
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Raw Data View */}
          {activeTab === 'raw-data' && isClinician && (
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Raw Device Data
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Showing{' '}
                      {totalRows > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}{' '}
                      - {Math.min(currentPage * rowsPerPage, totalRows)} of{' '}
                      {totalRows} records
                    </p>
                  </div>
                </div>
              </div>

              {rawDataRows.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-highlight-green">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            pH (mV)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            pH Value
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            Temp (Raw)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            Temp (°C)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            Battery (mV)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {rawDataRows.map((row, idx) => (
                          <tr key={idx}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(row.recorded_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.ph_voltage_mv}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.calibrated_ph.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.temperature_raw}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.calibrated_temp.toFixed(1)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.battery_voltage_mv}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              0x
                              {row.status_bits?.toString(16).padStart(2, '0') ||
                                '00'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage(
                            Math.min(
                              Math.ceil(totalRows / rowsPerPage),
                              currentPage + 1
                            )
                          )
                        }
                        disabled={
                          currentPage >= Math.ceil(totalRows / rowsPerPage)
                        }
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Page{' '}
                          <span className="font-medium">{currentPage}</span> of{' '}
                          <span className="font-medium">
                            {Math.ceil(totalRows / rowsPerPage)}
                          </span>
                        </p>
                      </div>
                      <div>
                        <nav
                          className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                          aria-label="Pagination"
                        >
                          <button
                            onClick={() =>
                              setCurrentPage(Math.max(1, currentPage - 1))
                            }
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Previous</span>
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>

                          {/* Page numbers */}
                          {Array.from(
                            {
                              length: Math.min(
                                5,
                                Math.ceil(totalRows / rowsPerPage)
                              )
                            },
                            (_, i) => {
                              const totalPages = Math.ceil(
                                totalRows / rowsPerPage
                              )
                              let pageNum

                              if (totalPages <= 5) {
                                pageNum = i + 1
                              } else if (currentPage <= 3) {
                                pageNum = i + 1
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i
                              } else {
                                pageNum = currentPage - 2 + i
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    currentPage === pageNum
                                      ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              )
                            }
                          )}

                          <button
                            onClick={() =>
                              setCurrentPage(
                                Math.min(
                                  Math.ceil(totalRows / rowsPerPage),
                                  currentPage + 1
                                )
                              )
                            }
                            disabled={
                              currentPage >= Math.ceil(totalRows / rowsPerPage)
                            }
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Next</span>
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-gray-500">No raw data available</p>
                </div>
              )}
            </div>
          )}

          {/* Access denied for non-clinicians */}
          {!isClinician && (
            <div className="bg-white rounded-3xl shadow-sm p-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <p className="text-sm text-gray-500">
                Access to patient health data is restricted to clinicians only
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
